import React from 'react';
import { API_BASE, toUrl } from '../lib/config';

type Row = {
  id: string;
  page: string;
  missionTitle?: string | null;
  missionText?: string | null;
  visionTitle?: string | null;
  visionText?: string | null;
  goalsAccentTitle?: string | null;
  goalsRestTitle?: string | null;
  goalsItems?: string[];
  corporateAccentTitle?: string | null;
  corporateRestTitle?: string | null;
  corporateText?: string | null;
  logoUrl?: string | null;
  isActive: boolean;
  updatedAt?: string;
};

type MediaOpt = { id?: string; name: string; url: string };

const VisionMissionYonetim: React.FC = () => {
  
  const PAGE = 'vizyon-misyon';

  const [items, setItems] = React.useState<Row[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [toast, setToast] = React.useState<{ show: boolean; text: string }>({ show: false, text: '' });

  const [selectedId, setSelectedId] = React.useState('');
  const [missionTitle, setMissionTitle] = React.useState('');
  const [missionText, setMissionText] = React.useState('');
  const [visionTitle, setVisionTitle] = React.useState('');
  const [visionText, setVisionText] = React.useState('');
  const [goalsAccentTitle, setGoalsAccentTitle] = React.useState('');
  const [goalsRestTitle, setGoalsRestTitle] = React.useState('');
  const [goalsItems, setGoalsItems] = React.useState<string[]>([]);
  const [corporateAccentTitle, setCorporateAccentTitle] = React.useState('');
  const [corporateRestTitle, setCorporateRestTitle] = React.useState('');
  const [corporateText, setCorporateText] = React.useState('');
  const [logo, setLogo] = React.useState('');
  const [active, setActive] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const [mediaOpts, setMediaOpts] = React.useState<MediaOpt[]>([]);

  // Media helpers: keep select value as UUID to match option values and show names
  const extractUuid = (u?: string | null) => {
    if (!u) return '';
    const m = u.match(/([0-9a-fA-F-]{36})$/);
    return m ? m[1] : u; // if not uuid, keep original
  };
  const absoluteLogoUrl = (val?: string | null) => {
    if (!val) return null;
    const uuidRe = /^[0-9a-fA-F-]{36}$/;
    const raw = uuidRe.test(val) ? `/api/media/${val}` : val;
    return /^https?:\/\//i.test(raw) ? raw : toUrl(raw);
  };

  // Save as relative '/api/media/:uuid' path
  const normalizeLogoForSave = (val?: string | null): string | null => {
    if (!val) return null;
    const s = String(val).trim();
    // match absolute or relative /api/media/<uuid>
    let m = s.match(/\/api\/media\/([0-9a-fA-F-]{36})$/);
    if (m) return `/api/media/${m[1]}`;
    // match bare uuid
    m = s.match(/([0-9a-fA-F-]{36})$/);
    if (m) return `/api/media/${m[1]}`;
    return s;
  };

  const ImgWithFallback: React.FC<{ src?: string | null; alt: string; height?: number }> = ({ src, alt, height = 56 }) => {
    const baseIn = src || '';
    const m = baseIn.match(/([0-9a-fA-F-]{36})$/);
    const raw = m ? `/api/media/${m[1]}` : baseIn;
    const pref = toUrl(raw);
    const [currentSrc, setCurrentSrc] = React.useState(pref);
    return (
      <img
        src={currentSrc}
        alt={alt}
        style={{ height, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff' }}
        crossOrigin="anonymous"
        onError={(e) => {
          const el = e.currentTarget as HTMLImageElement;
          const cur = el.getAttribute('src') || '';
          const next = cur === pref ? raw : pref;
          if (next !== cur) el.src = next;
        }}
      />
    );
  };

  const fetchAll = React.useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await fetch(toUrl(`/api/vision-mission/admin/${PAGE}`));
      if (!res.ok) throw new Error('Kayıtlar alınamadı');
      const data = await res.json();
      setItems(data || []);
      const initial = (data && data[0]) || null;
      if (initial) {
        setSelectedId(initial.id);
        setMissionTitle(initial.missionTitle || '');
        setMissionText(initial.missionText || '');
        setVisionTitle(initial.visionTitle || '');
        setVisionText(initial.visionText || '');
        setGoalsAccentTitle(initial.goalsAccentTitle || '');
        setGoalsRestTitle(initial.goalsRestTitle || '');
        setGoalsItems([...((initial.goalsItems as any) || [])]);
        setCorporateAccentTitle(initial.corporateAccentTitle || '');
        setCorporateRestTitle(initial.corporateRestTitle || '');
        setCorporateText(initial.corporateText || '');
        // normalize to UUID so dropdown displays media name
        setLogo(extractUuid(initial.logoUrl));
        setActive(!!initial.isActive);
      } else {
        setSelectedId('');
      }
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoading(false); }
  }, []);

  const fetchMedia = React.useCallback(async () => {
    try {
      const res = await fetch(toUrl('/api/media/admin'));
      if (!res.ok) return;
      const data = await res.json();
      setMediaOpts((data || []).map((m: any) => ({ id: m.id, name: m.name || m.originalName || m.id || m.url, url: m.url || `/api/media/${m.id}` })));
    } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { fetchAll(); fetchMedia(); }, [fetchAll, fetchMedia]);

  const onSelect = (id: string) => {
    setSelectedId(id);
    const it = items.find(x => x.id === id);
    if (!it) return;
    setMissionTitle(it.missionTitle || '');
    setMissionText(it.missionText || '');
    setVisionTitle(it.visionTitle || '');
    setVisionText(it.visionText || '');
    setGoalsAccentTitle(it.goalsAccentTitle || '');
    setGoalsRestTitle(it.goalsRestTitle || '');
    setGoalsItems([...(it.goalsItems || [])]);
    setCorporateAccentTitle(it.corporateAccentTitle || '');
    setCorporateRestTitle(it.corporateRestTitle || '');
    setCorporateText(it.corporateText || '');
    setLogo(extractUuid(it.logoUrl));
    setActive(!!it.isActive);
  };

  // Persist the whole record
  const persist = async (payload: Partial<Row>) => {
    if (!selectedId) { setError('Düzenlenecek kayıt yok'); return; }
    try {
      setSaving(true); setError('');
      const body: any = {
        missionTitle: missionTitle || null,
        missionText: missionText || null,
        visionTitle: visionTitle || null,
        visionText: visionText || null,
        goalsAccentTitle: goalsAccentTitle || null,
        goalsRestTitle: goalsRestTitle || null,
        goalsItems: goalsItems,
        corporateAccentTitle: corporateAccentTitle || null,
        corporateRestTitle: corporateRestTitle || null,
        corporateText: corporateText || null,
        // persist as relative media path
        logoUrl: normalizeLogoForSave(logo),
        isActive: !!active,
        ...payload,
      };
      const res = await fetch(toUrl(`/api/vision-mission/admin/${selectedId}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      await fetchAll();
      setToast({ show: true, text: 'Başarılı' });
      window.setTimeout(() => setToast({ show: false, text: '' }), 2000);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSaving(false); }
  };

  // goalsItems CRUD (client-side array; persist immediately)
  const [newGoal, setNewGoal] = React.useState('');
  const addGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGoal.trim()) return;
    const next = [...goalsItems, newGoal.trim()];
    setGoalsItems(next);
    setNewGoal('');
    await persist({ goalsItems: next });
  };
  const removeGoal = async (idx: number) => {
    const next = goalsItems.filter((_, i) => i !== idx);
    setGoalsItems(next);
    await persist({ goalsItems: next });
  };
  const [editingGoalIdx, setEditingGoalIdx] = React.useState<number | null>(null);
  const [editingGoalText, setEditingGoalText] = React.useState('');
  const startEditGoal = (idx: number) => { setEditingGoalIdx(idx); setEditingGoalText(goalsItems[idx] || ''); };
  const cancelEditGoal = () => { setEditingGoalIdx(null); setEditingGoalText(''); };
  const saveGoal = async () => {
    if (editingGoalIdx === null) return;
    const next = goalsItems.map((g, i) => (i === editingGoalIdx ? editingGoalText.trim() : g)).filter(Boolean);
    setGoalsItems(next);
    setEditingGoalIdx(null); setEditingGoalText('');
    await persist({ goalsItems: next });
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Vizyon & Misyon – {PAGE}</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Kayıt: {items.length}</div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontWeight: 600 }}>Alanlar</div>
          <button onClick={fetchAll} className="btn">Yenile</button>
        </div>

        {items.length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <label className="form-label" style={{ display: 'block', marginBottom: 6 }}>Kayıt Seç</label>
            <select className="input" value={selectedId} onChange={(e) => onSelect(e.target.value)}>
              {items.map(it => (
                <option key={it.id} value={it.id}>{it.page} – {it.missionTitle || it.visionTitle || it.id}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          {/* Logo */}
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Logo</label>
            <div style={{ display: 'grid', gap: 6 }}>
              <select className="input" value={logo} onChange={(e) => setLogo(e.target.value)}>
                <option value="">Seçiniz</option>
                {mediaOpts.map(m => (
                  <option key={(m.id || m.url)} value={(m.id || m.url)}>{m.name}</option>
                ))}
              </select>
              {logo && (<ImgWithFallback src={logo} alt="logo" height={56} />)}
            </div>
          </div>
          {/* Mission */}
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Misyon Başlık</label>
            <input className="input" value={missionTitle} onChange={(e) => setMissionTitle(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Misyon Metni</label>
            <textarea className="input" rows={3} value={missionText} onChange={(e) => setMissionText(e.target.value)} />
          </div>
          {/* Vision */}
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Vizyon Başlık</label>
            <input className="input" value={visionTitle} onChange={(e) => setVisionTitle(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Vizyon Metni</label>
            <textarea className="input" rows={3} value={visionText} onChange={(e) => setVisionText(e.target.value)} />
          </div>
          {/* Goals headers */}
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Amaçlar (Vurgu Başlığı)</label>
            <input className="input" value={goalsAccentTitle} onChange={(e) => setGoalsAccentTitle(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Amaçlar (Diğer Başlık)</label>
            <input className="input" value={goalsRestTitle} onChange={(e) => setGoalsRestTitle(e.target.value)} />
          </div>

          {/* Goals items CRUD */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontWeight: 600 }}>Amaçlar ve Hedefler (Maddeler)</div>
            </div>
            <form onSubmit={addGoal} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 12 }}>
              <input className="input" placeholder="Yeni madde" value={newGoal} onChange={(e) => setNewGoal(e.target.value)} />
              <button type="submit" className="btn btn-primary" disabled={!newGoal.trim()}>Ekle</button>
            </form>
            <div>
              {goalsItems.length === 0 && <div style={{ color: '#6b7280' }}>Henüz madde yok.</div>}
              {goalsItems.map((g, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'center', padding: '8px 0', borderTop: idx === 0 ? '1px solid #e5e7eb' : undefined, borderBottom: '1px solid #e5e7eb' }}>
                  {editingGoalIdx === idx ? (
                    <textarea className="input" rows={2} value={editingGoalText} onChange={(e) => setEditingGoalText(e.target.value)} />
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{g}</div>
                  )}
                  {editingGoalIdx === idx ? (
                    <button className="btn btn-primary" onClick={saveGoal} disabled={!editingGoalText.trim()}>Kaydet</button>
                  ) : (
                    <button className="btn" onClick={() => startEditGoal(idx)}>Düzenle</button>
                  )}
                  {editingGoalIdx === idx ? (
                    <button className="btn" onClick={cancelEditGoal}>İptal</button>
                  ) : (
                    <button className="btn" style={{ background: '#dc2626', color: '#fff', border: '1px solid #dc2626' }} onClick={() => removeGoal(idx)}>Sil</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Corporate */}
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Kurumsal (Vurgu Başlığı)</label>
            <input className="input" value={corporateAccentTitle} onChange={(e) => setCorporateAccentTitle(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Kurumsal (Diğer Başlık)</label>
            <input className="input" value={corporateRestTitle} onChange={(e) => setCorporateRestTitle(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Kurumsal Metin</label>
            <textarea className="input" rows={4} value={corporateText} onChange={(e) => setCorporateText(e.target.value)} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input id="act" type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <label htmlFor="act">Aktif</label>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={() => persist({})} disabled={saving || !selectedId} className="btn btn-primary">{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
        </div>

        {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
      </div>

      {toast.show && (
        <div role="status" aria-live="polite" style={{ position: 'fixed', right: 16, bottom: 16, background: '#16a34a', color: '#fff', padding: '10px 14px', borderRadius: 8, boxShadow: '0 10px 20px rgba(0,0,0,0.15)' }}>
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default VisionMissionYonetim;
